import React, { useState, useEffect } from "react";
import { Modal, Box } from "../../../components/ui";
import { campusApi } from "../campus-api";
import { Camera } from "../types";
import { modalStyle } from "../../../style/common";

interface CameraLiveFeedModalProps {
  open: boolean;
  onClose: () => void;
  camera: Camera | null;
}

export default function CameraLiveFeedModal({ open, onClose, camera }: CameraLiveFeedModalProps) {
  const [streamUrl, setStreamUrl] = useState<string>("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>("");

  useEffect(() => {
    if (open && camera) {
      fetchLiveFeed();
    } else {
      setStreamUrl("");
      setError("");
    }
  }, [open, camera]);

  const fetchLiveFeed = async () => {
    if (!camera) return;
    
    setLoading(true);
    setError("");
    try {
      const url = await campusApi.getCameraLiveFeed(camera.id);
      setStreamUrl(url);
    } catch (err: any) {
      setError(err?.response?.data?.message || "Failed to load live feed");
    } finally {
      setLoading(false);
    }
  };

  if (!camera) return null;

  return (
    <Modal onClose={onClose} open={open}>
      <Box sx={{ ...modalStyle, maxWidth: "90vw", width: "1200px" }}>
        <div className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              Live Feed - Camera #{camera.id}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
            >
              <i className="fa-solid fa-times text-xl"></i>
            </button>
          </div>

          <div className="bg-black rounded-lg overflow-hidden aspect-video flex items-center justify-center">
            {loading ? (
              <div className="flex flex-col items-center gap-4">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-white"></div>
                <p className="text-white">Loading stream...</p>
              </div>
            ) : error ? (
              <div className="flex flex-col items-center gap-4 p-8">
                <i className="fa-solid fa-exclamation-triangle text-4xl text-yellow-400"></i>
                <p className="text-white text-center">{error}</p>
                <button
                  onClick={fetchLiveFeed}
                  className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                >
                  Retry
                </button>
              </div>
            ) : streamUrl ? (
              <video
                src={streamUrl}
                autoPlay
                controls
                className="w-full h-full object-contain"
                onError={() => {
                  setError("Failed to load video stream");
                }}
              />
            ) : (
              <div className="flex flex-col items-center gap-4">
                <i className="fa-solid fa-video-slash text-4xl text-gray-400"></i>
                <p className="text-white">No stream available</p>
              </div>
            )}
          </div>

          <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-lg">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-500 dark:text-gray-400">Camera Type</p>
                <p className="font-medium text-gray-900 dark:text-white capitalize">{camera.type}</p>
              </div>
              <div>
                <p className="text-gray-500 dark:text-gray-400">Vehicle Detection</p>
                <p className="font-medium text-gray-900 dark:text-white">
                  {camera.vehicleDetectionEnabled ? "Enabled" : "Disabled"}
                </p>
              </div>
              <div className="col-span-2">
                <p className="text-gray-500 dark:text-gray-400">RTSP URL</p>
                <p className="font-mono text-xs text-gray-900 dark:text-white break-all">{camera.rtspUrl}</p>
              </div>
            </div>
          </div>
        </div>
      </Box>
    </Modal>
  );
}

